/**
 * Relatório de fechamento da vaga — mostrado no detalhe da vaga quando ela
 * está encerrada (especialmente após uma contratação).
 *
 * Seções:
 * - Hero card: candidato contratado, score, salário (se cadastrado), days-to-hire
 * - Funil final: contagem por status com barra visual
 * - Critérios que pesaram: ranking_breakdown do contratado
 * - Avaliações aplicadas: testes do GridProfile com score
 *
 * Endpoint: GET /api/jobs/{id}/closure-report
 */
import axios from "axios";
import { useEffect, useState } from "react";
import {
  Trophy,
  Loader2,
  Calendar,
  TrendingUp,
  Users,
  Sparkles,
  Award,
  DollarSign,
} from "lucide-react";
import { API } from "@/context/AuthContext";

const STATUS_COLORS = {
  contratado: "bg-amber-500",
  aprovado: "bg-emerald-500",
  em_entrevista: "bg-blue-500",
  contatado: "bg-violet-500",
  analisado: "bg-slate-400",
  rejeitado: "bg-rose-400",
  pendente: "bg-slate-300",
  erro: "bg-slate-300",
};
const STATUS_LABEL = {
  contratado: "Contratado",
  aprovado: "Aprovado",
  em_entrevista: "Em entrevista",
  contatado: "Contatado",
  analisado: "Analisado",
  rejeitado: "Rejeitado",
  pendente: "Pendente",
  erro: "Erro",
};

const fmtCurrency = (v) => {
  if (v === null || v === undefined || v === "") return null;
  try {
    return Number(v).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  } catch {
    return `R$ ${v}`;
  }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
};

const JobClosureReport = ({ jobId }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/jobs/${jobId}/closure-report`)
      .then((res) => {
        if (!cancelled) setReport(res.data);
      })
      .catch(() => {
        if (!cancelled) setReport(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
        <Loader2 className="w-4 h-4 animate-spin" /> Gerando relatório…
      </div>
    );
  }

  if (!report || !report.job) return null;

  const hire = report.hire;
  const funnel = report.funnel || {};
  const total = funnel.total || 0;
  const time = report.time_metrics || {};

  // Funnel breakdown (exclude "total" key for the bars)
  const funnelEntries = Object.entries(funnel)
    .filter(([k]) => k !== "total")
    .sort((a, b) => b[1] - a[1]);

  return (
    <div
      data-testid="closure-report"
      className="bg-gradient-to-br from-amber-50 via-white to-amber-50 border-2 border-amber-300 rounded-xl overflow-hidden shadow-sm"
    >
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-5 py-3 flex items-center gap-2">
        <Trophy className="w-5 h-5" />
        <h3 className="text-sm font-bold uppercase tracking-wider">
          Relatório de fechamento
        </h3>
      </div>

      <div className="p-5 space-y-5">
        {/* Hero: contratado */}
        {hire ? (
          <div className="bg-white rounded-lg border border-amber-200 p-4">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shadow-md">
                <Trophy className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">
                  Candidato contratado
                </div>
                <div
                  data-testid="closure-hire-name"
                  className="text-lg font-bold text-slate-900"
                >
                  {hire.candidate_name || "—"}
                </div>
                <div className="text-xs text-slate-500">
                  {hire.candidate_email}
                </div>
                {hire.hire_notes && (
                  <p className="text-xs text-slate-700 italic mt-2 border-l-2 border-amber-300 pl-2">
                    "{hire.hire_notes}"
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-3xl font-bold text-amber-700 tabular-nums leading-none">
                  {(hire.ranking_score || hire.score || 0).toFixed(1)}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  Score
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              {hire.hired_salary && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-700 mx-auto mb-0.5" />
                  <div
                    data-testid="closure-salary"
                    className="text-sm font-bold text-emerald-800 tabular-nums"
                  >
                    {fmtCurrency(hire.hired_salary)}
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">
                    Salário
                  </div>
                </div>
              )}
              {time.days_to_hire !== null && time.days_to_hire !== undefined && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
                  <Calendar className="w-3.5 h-3.5 text-blue-700 mx-auto mb-0.5" />
                  <div className="text-sm font-bold text-blue-800 tabular-nums">
                    {time.days_to_hire} dias
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">
                    Time-to-hire
                  </div>
                </div>
              )}
              <div className="bg-violet-50 border border-violet-200 rounded p-2 text-center">
                <Users className="w-3.5 h-3.5 text-violet-700 mx-auto mb-0.5" />
                <div className="text-sm font-bold text-violet-800 tabular-nums">
                  {total}
                </div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">
                  Candidatos
                </div>
              </div>
            </div>
            {hire.hired_at && (
              <div className="text-[10px] text-slate-500 mt-2 text-right">
                Contratado em {fmtDate(hire.hired_at)}
                {hire.hired_by_user_name && ` por ${hire.hired_by_user_name}`}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 p-4 text-sm text-slate-500 italic">
            Esta vaga foi encerrada sem registro de contratação.
            {report.job.closed_by_user_name && (
              <span className="block text-[11px] mt-1">
                Encerrada por {report.job.closed_by_user_name} em{" "}
                {fmtDate(report.job.closed_at)}
              </span>
            )}
          </div>
        )}

        {/* Funnel breakdown */}
        {funnelEntries.length > 0 && (
          <div>
            <h4 className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Funil final
            </h4>
            <ul className="space-y-1.5">
              {funnelEntries.map(([key, count]) => {
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <li
                    key={key}
                    data-testid={`closure-funnel-${key}`}
                    className="flex items-center gap-2 text-xs"
                  >
                    <span className="w-28 shrink-0 text-slate-700">
                      {STATUS_LABEL[key] || key}
                    </span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full ${STATUS_COLORS[key] || "bg-slate-400"} rounded-full transition-all`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-16 shrink-0 text-right text-slate-600 tabular-nums">
                      {count} ({pct}%)
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Top criteria — only if we have a hire with breakdown */}
        {report.top_criteria && report.top_criteria.length > 0 && (
          <div>
            <h4 className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> Critérios que pesaram
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {report.top_criteria.map((c) => (
                <div
                  key={c.label}
                  className="bg-white border border-slate-200 rounded p-2"
                >
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">
                    {c.label}
                  </div>
                  <div className="text-base font-bold text-slate-900 tabular-nums">
                    {Number(c.value).toFixed(1)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assessments */}
        {report.assessments && report.assessments.length > 0 && (
          <div>
            <h4 className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Testes aplicados
            </h4>
            <ul className="space-y-1.5">
              {report.assessments.map((a, idx) => (
                <li
                  key={idx}
                  className="flex items-center gap-2 bg-white border border-slate-200 rounded p-2"
                >
                  <Award className="w-4 h-4 text-violet-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-900 truncate">
                      {a.test_name}
                    </div>
                    {a.score_label && (
                      <div className="text-[10px] text-slate-500 truncate">
                        {a.score_label}
                      </div>
                    )}
                  </div>
                  {a.score !== null && a.score !== undefined ? (
                    <span className="text-xs font-bold text-violet-700 tabular-nums shrink-0">
                      {Number(a.score).toFixed(1)}
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400 italic shrink-0">
                      pendente
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobClosureReport;
